import { Title, Text, Stack, Group, Box } from "@mantine/core";
import { scriptsQueryOptions } from "@/api/fapinstructor/scripts/get-scripts";
import { TbFileText } from "react-icons/tb";
import { EmptyState } from "@/components/empty-state";
import { ScriptCard } from "@/components/script/script-card";
import { useHasScriptFeatureAccess } from "@/hooks/use-has-script-feature-access";
import { ScriptUpgradeMembershipTierBanner } from "@/components/script/script-upgrade-membership-tier-banner";
import { Masonry } from "masonic";
import type { Script } from "@fapinstructor/common";
import { createFileRoute } from "@tanstack/react-router";
import { Page } from "@/components/page";
import { CreateScriptCard } from "./-components/create-script-card";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/_main/my-scripts/",
)({
  loader: (opts) =>
    opts.context.queryClient.ensureInfiniteQueryData(
      scriptsQueryOptions({
        userId: opts.context.auth.userId,
        limit: 1000,
      }),
    ),
  component: MyScriptsRouteComponent,
});

function MyScriptsRouteComponent() {
  const { auth } = Route.useRouteContext();
  const scriptsQuery = useSuspenseInfiniteQuery(
    scriptsQueryOptions({
      userId: auth.userId,
      limit: 1000,
    }),
  );
  const scripts = scriptsQuery.data.pages.flatMap((p) => p.data);
  const hasScriptFeatureAccess = useHasScriptFeatureAccess();

  const publishedScripts = scripts.filter((script) => script.published);
  const unpublishedScripts = scripts.filter((script) => !script.published);

  return (
    <Page title="Your Scripts">
      <Group justify="space-between">
        <Title>Your Scripts</Title>
        <CreateScriptCard />
      </Group>
      <Stack mt="xl">
        {!hasScriptFeatureAccess ? <ScriptUpgradeMembershipTierBanner /> : null}
        {scripts.length === 0 ? (
          <EmptyState
            Icon={TbFileText}
            title="No scripts found"
            description='Click "Create Script" above to get started.'
          />
        ) : (
          <>
            <Box>
              <Title order={2}>Private Scripts</Title>
              <Text>
                These scripts are private and can only be viewed by you.
              </Text>
            </Box>
            {unpublishedScripts.length === 0 ? (
              <Box w="100%">
                <EmptyState
                  Icon={TbFileText}
                  title="No scripts found"
                  description='Click "Create Script" above to get started.'
                />
              </Box>
            ) : null}
            <Masonry
              key={crypto.randomUUID()}
              items={unpublishedScripts}
              columnGutter={16}
              columnWidth={300}
              render={MasonryCard}
            />
            <Box mt="xl">
              <Title order={2}>Published Scripts</Title>
              <Text>
                These scripts are shared and viewable within the Game Generator
                page.
              </Text>
            </Box>
            <Masonry
              key={crypto.randomUUID()}
              items={publishedScripts}
              columnGutter={16}
              columnWidth={300}
              render={MasonryCard}
            />
          </>
        )}
      </Stack>
    </Page>
  );
}

type MasonryCardProps = { data: Script };

function MasonryCard({ data: script }: MasonryCardProps) {
  return <ScriptCard key={script.id} script={script} />;
}
